import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { doc, getDoc, collection, query, where, orderBy, getDocs, updateDoc } from 'firebase/firestore';
import Navbar from '../components/Navbar';

const Profile = () => {
  const { currentUser, userDetails } = useAuth();
  
  // User profile data
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [province, setProvince] = useState<string>('');
  const [createdAt, setCreatedAt] = useState<string>('');
  const [teamName, setTeamName] = useState<string>('');
  const [userTotalMiles, setUserTotalMiles] = useState<number>(0);
  
  // Fundraising data
  const [totalRaised, setTotalRaised] = useState<number>(0);
  const [donationCount, setDonationCount] = useState<number>(0);
  
  // Miles history (grouped by month)
  const [milesHistory, setMilesHistory] = useState<any[]>([]);
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  
  // Canadian provinces and territories data (for province selection)
  const provinces = [
    { code: 'AB', name: 'Alberta' },
    { code: 'BC', name: 'British Columbia' },
    { code: 'MB', name: 'Manitoba' },
    { code: 'NB', name: 'New Brunswick' },
    { code: 'NL', name: 'Newfoundland and Labrador' },
    { code: 'NS', name: 'Nova Scotia' },
    { code: 'NT', name: 'Northwest Territories' },
    { code: 'NU', name: 'Nunavut' },
    { code: 'ON', name: 'Ontario' },
    { code: 'PE', name: 'Prince Edward Island' },
    { code: 'QC', name: 'Quebec' },
    { code: 'SK', name: 'Saskatchewan' },
    { code: 'YT', name: 'Yukon' }
  ];
  
  // New state variables
  const [emailNotifications, setEmailNotifications] = useState<boolean>(false);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [saving, setSaving] = useState<boolean>(false);
  const [settingsMessage, setSettingsMessage] = useState<string>('');
  const [settingsSuccess, setSettingsSuccess] = useState<boolean>(false);
  
  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser || !userDetails) return;
      
      try {
        // Get user details from the auth context
        setFirstName(userDetails.firstName || '');
        setLastName(userDetails.lastName || '');
        setProvince(userDetails.province || '');
        setCreatedAt(new Date(userDetails.createdAt || Date.now()).toLocaleDateString());
        setTotalRaised(userDetails.totalRaised || 0);
        
        // Get team information if user is in a team
        if (userDetails.teamId) {
          const teamDoc = await getDoc(doc(db, 'teams', userDetails.teamId));
          if (teamDoc.exists()) {
            const teamData = teamDoc.data();
            setTeamName(teamData.name || 'Unknown Team');
          }
        }
        
        // Get user's miles
        const milesRef = collection(db, 'miles');
        const userMilesQuery = query(
          milesRef,
          where('userId', '==', currentUser.uid),
          orderBy('date', 'desc')
        );
        const milesSnapshot = await getDocs(userMilesQuery);
        
        let total = 0;
        const milesHistoryMap = new Map();
        
        milesSnapshot.forEach((doc) => {
          const data = doc.data();
          total += Number(data.miles);
          
          // Group miles by month
          const date = new Date(data.date);
          const monthKey = `${date.getFullYear()}-${date.getMonth() + 1}`;
          const monthName = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
          
          if (milesHistoryMap.has(monthKey)) {
            const monthData = milesHistoryMap.get(monthKey);
            monthData.miles += Number(data.miles);
            monthData.entries += 1;
          } else {
            milesHistoryMap.set(monthKey, {
              month: monthName,
              miles: Number(data.miles),
              entries: 1
            });
          }
        });
        
        setUserTotalMiles(total);
        
        // Convert map to array and sort by date (newest first)
        const milesHistoryArray = Array.from(milesHistoryMap.entries())
          .map(([key, value]) => ({ 
            key, 
            month: value.month, 
            miles: value.miles, 
            entries: value.entries 
          }))
          .sort((a, b) => b.key.localeCompare(a.key));
        
        setMilesHistory(milesHistoryArray);
        
        // Get donation count
        const donationsRef = collection(db, 'donations');
        const userDonationsQuery = query(
          donationsRef,
          where('referrerId', '==', currentUser.uid)
        );
        const donationsSnapshot = await getDocs(userDonationsQuery);
        setDonationCount(donationsSnapshot.size);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [currentUser, userDetails]);
  
  // Save profile changes
  const handleSaveProfile = async () => {
    if (!currentUser) return;
    
    try {
      setSaveLoading(true);
      setError('');
      
      // Update user document in Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        firstName,
        lastName,
        province
      });
      
      setIsEditing(false);
      setSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile');
    } finally {
      setSaveLoading(false);
    }
  };
  
  // Cancel edit mode
  const handleCancel = () => {
    // Reset form to current user details
    setFirstName(userDetails?.firstName || '');
    setLastName(userDetails?.lastName || '');
    setProvince(userDetails?.province || '');
    setIsEditing(false);
    setError('');
  };
  
  // Handle theme change
  const handleThemeChange = (newTheme: 'light' | 'dark') => {
    setTheme(newTheme);
    // If you want to apply the theme immediately:
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };
  
  // Handle saving settings
  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setSettingsMessage('');
      
      if (currentUser) {
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, {
          emailNotifications,
          theme,
          updatedAt: new Date().toISOString()
        });
        
        setSettingsSuccess(true);
        setSettingsMessage('Settings saved successfully');
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSettingsMessage('');
        }, 3000);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setSettingsSuccess(false);
      setSettingsMessage('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  // Load user settings when the component mounts
  useEffect(() => {
    const loadUserSettings = async () => {
      if (currentUser && userDetails) {
        setEmailNotifications(userDetails.emailNotifications || false);
        setTheme(userDetails.theme || 'light');
        
        // Apply theme
        if (userDetails.theme === 'dark') {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }
    };
    
    loadUserSettings();
  }, [currentUser, userDetails]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Your Profile</h1>
          <p className="mt-1 text-sm text-gray-500">
            View and manage your personal information
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Profile Section */}
          <div className="md:col-span-2">
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Profile Information</h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">Personal details</p>
                </div>
                {!isEditing && (
                  <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    Edit
                  </button>
                )}
              </div>
              
              {error && (
                <div className="px-4 py-3 bg-red-50 border-t border-b border-red-200">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
              
              {success && (
                <div className="px-4 py-3 bg-green-50 border-t border-b border-green-200">
                  <p className="text-sm text-green-700">Profile updated successfully!</p>
                </div>
              )}
              
              <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                {isEditing ? (
                  <form className="space-y-6">
                    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-3">
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                          First name
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            name="firstName"
                            id="firstName"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                      
                      <div className="sm:col-span-3">
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                          Last name
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            name="lastName"
                            id="lastName"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                      
                      <div className="sm:col-span-3">
                        <label htmlFor="province" className="block text-sm font-medium text-gray-700">
                          Province/Territory
                        </label>
                        <div className="mt-1">
                          <select
                            id="province"
                            name="province"
                            value={province}
                            onChange={(e) => setProvince(e.target.value)}
                            className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                          >
                            <option value="">Select Province/Territory</option>
                            {provinces.map((p) => (
                              <option key={p.code} value={p.code}>
                                {p.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveProfile}
                        disabled={saveLoading}
                        className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                      >
                        {saveLoading ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </form>
                ) : (
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500">Full name</dt>
                      <dd className="mt-1 text-sm text-gray-900">{`${firstName} ${lastName}`}</dd>
                    </div>
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500">Email address</dt>
                      <dd className="mt-1 text-sm text-gray-900">{currentUser?.email}</dd>
                    </div>
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500">Province/Territory</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {provinces.find(p => p.code === province)?.name || province}
                      </dd>
                    </div>
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500">Joined</dt>
                      <dd className="mt-1 text-sm text-gray-900">{createdAt}</dd>
                    </div>
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500">Team</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {teamName || 'Not part of a team'}
                      </dd>
                    </div>
                  </dl>
                )}
              </div>
            </div>
            
            {/* Fundraising Progress Section */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg mt-6">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Fundraising Progress</h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Track your fundraising impact
                </p>
              </div>
              <div className="border-t border-gray-200 p-6">
                <div className="mb-6">
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Total Raised</span>
                    <span className="text-sm font-medium text-gray-900">${totalRaised.toFixed(2)}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-secondary h-2.5 rounded-full" 
                      style={{ width: `${Math.min(100, (totalRaised / 1000) * 100)}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-gray-500">$0</span>
                    <span className="text-xs text-gray-500">Goal: $1,000</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-secondary">${totalRaised.toFixed(2)}</p>
                    <p className="text-sm text-gray-500 mt-1">Total Raised</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-secondary">{donationCount}</p>
                    <p className="text-sm text-gray-500 mt-1">Donations Received</p>
                  </div>
                </div>
                
                <div className="mt-4 text-center">
                  <p className="text-sm text-gray-600 mb-4">
                    Share your fundraising page with friends and family to increase your impact!
                  </p>
                  <a 
                    href="/invite-friends" 
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-secondary hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                  >
                    Invite Others to Donate
                  </a>
                </div>
              </div>
            </div>
            
            {/* Miles History */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg mt-6">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Miles History</h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Monthly breakdown of your walking miles
                </p>
              </div>
              <div className="border-t border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Month
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Miles
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Entries
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {milesHistory.length > 0 ? (
                      milesHistory.map((month) => (
                        <tr key={month.key}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {month.month}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            {month.miles.toFixed(1)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                            {month.entries}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                          No miles logged yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <th scope="row" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                      <td className="px-6 py-3 text-right text-xs font-medium text-gray-900">
                        {userTotalMiles.toFixed(1)} miles
                      </td>
                      <td className="px-6 py-3 text-right text-xs font-medium text-gray-500">
                        {milesHistory.reduce((total, month) => total + month.entries, 0)} entries
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
          
          {/* Stats Card */}
          <div>
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6 bg-primary bg-opacity-10">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Your Stats</h3>
              </div>
              <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                <div className="space-y-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Total Miles</p>
                    <p className="mt-1 text-3xl font-bold text-primary">{userTotalMiles.toFixed(1)}</p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Fundraising</p>
                    <p className="mt-1 text-3xl font-bold text-secondary">${totalRaised.toFixed(2)}</p>
                    <p className="text-xs text-gray-500 mt-1">Goal: $1,000</p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Monthly Average</h4>
                    <p className="mt-1 text-2xl font-semibold text-secondary">
                      {milesHistory.length > 0 
                        ? (userTotalMiles / milesHistory.length).toFixed(1) 
                        : '0.0'} miles
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Months Active</h4>
                    <p className="mt-1 text-xl font-medium text-accent">{milesHistory.length}</p>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-200">
                    <h4 className="text-sm font-medium text-gray-500">Quick Links</h4>
                    <div className="mt-2 space-y-3">
                      <a 
                        href="/log-miles" 
                        className="block w-full py-2 px-4 rounded bg-secondary text-white text-center font-medium hover:bg-opacity-90"
                      >
                        Log Today's Miles
                      </a>
                      <a 
                        href="/invite-friends" 
                        className="block w-full py-2 px-4 rounded bg-primary text-white text-center font-medium hover:bg-opacity-90"
                      >
                        Invite Friends
                      </a>
                      {userDetails?.teamId && (
                        <a 
                          href="/team" 
                          className="block w-full py-2 px-4 rounded bg-white border border-gray-300 text-gray-700 text-center font-medium hover:bg-gray-50"
                        >
                          View Your Team
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* User Settings */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg mt-6">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">User Settings</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Customize your experience
            </p>
          </div>
          <div className="border-t border-gray-200">
            <dl>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Email Notifications</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <div className="flex items-center">
                    <input
                      id="email-notifications"
                      name="email-notifications"
                      type="checkbox"
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      checked={emailNotifications}
                      onChange={() => setEmailNotifications(!emailNotifications)}
                    />
                    <label htmlFor="email-notifications" className="ml-2 block text-sm text-gray-900">
                      Receive emails for Million Miles May challenge
                    </label>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    You will automatically receive important updates and reminders about the Million Miles May challenge. Uncheck this box if you want to opt out.
                  </p>
                </dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Theme Preference</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <div className="flex items-center space-x-4">
                    <div 
                      className={`flex items-center p-2 border rounded-md cursor-pointer ${
                        theme === 'light' ? 'border-primary bg-blue-50' : 'border-gray-300'
                      }`}
                      onClick={() => handleThemeChange('light')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      <span>Light Mode</span>
                    </div>
                    <div 
                      className={`flex items-center p-2 border rounded-md cursor-pointer ${
                        theme === 'dark' ? 'border-primary bg-blue-50' : 'border-gray-300'
                      }`}
                      onClick={() => handleThemeChange('dark')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                      </svg>
                      <span>Dark Mode</span>
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Choose your preferred theme for the application.
                  </p>
                </dd>
              </div>
              <div className="bg-gray-50 px-4 py-5 sm:px-6">
                <button
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
                {settingsMessage && (
                  <p className={`mt-2 text-sm ${settingsSuccess ? 'text-green-600' : 'text-red-600'}`}>
                    {settingsMessage}
                  </p>
                )}
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile; 